import { lazy, Suspense, useId, useState } from 'react'
import { Banner, Button, Card, Drawer, Skeleton, StatusBadge } from '@shared/design-system/ui'
import { useKycVerification, useSyncKycVerification } from '../api/verification'
import { onboardingStepStatus } from '../model/onboarding-status'
import {
  actionRow,
  copy,
  panel,
  requirementItem,
  requirementList,
  completedSummary,
  neutralFeedback,
  section,
} from './verification-panel.css'
import { formCard, sectionCopy, sectionHeader, sectionTitle } from './onboarding.css'
import { feedbackFromView, useEmbeddedOnboarding } from './useEmbeddedOnboarding'
import { BankAccountSection } from './BankAccountSection'
import { useSession } from '@shared/lib/auth/session-store'

const LazyEmbeddedOnboarding = lazy(() =>
  import('./EmbeddedOnboarding').then((module) => ({ default: module.EmbeddedOnboarding })),
)

export const IDENTITY_SECTION_ANCHOR = 'verificacao-identidade'

type VerificationQuery = ReturnType<typeof useKycVerification>

type VerificationPanelProps = {
  verification?: VerificationQuery
  enabled?: boolean
  activationPending?: boolean
  showBankAccount?: boolean
  onEditProfile?: () => void
}

/**
 * The embedded flow lives outside the step sections: a background refresh that switches the
 * section to loading, error or unavailable must not unmount an open flow mid-session.
 */
export function VerificationPanel(props: VerificationPanelProps = {}) {
  const embedded = useEmbeddedOnboarding()
  const ownVerification = useKycVerification(props.verification === undefined)
  const verification = props.verification ?? ownVerification
  return (
    <>
      {embedded.registrationFeedbackEnabled &&
      embedded.error &&
      !embedded.isOpen &&
      verification.data?.onboarding.steps.identity !== 'done' ? (
        <Banner
          tone="danger"
          title="Não foi possível abrir a verificação de identidade"
          data-testid="onboarding-identity-error"
        >
          {embedded.error}
        </Banner>
      ) : null}
      <VerificationSections {...props} verification={verification} embedded={embedded} />
      <Drawer
        open={embedded.isOpen}
        onClose={() => void embedded.close()}
        title="Verificação de identidade"
        size="standard"
        variant="form"
      >
        {embedded.registrationFeedbackEnabled && embedded.error ? (
          <Banner
            tone="danger"
            title="Não foi possível abrir a verificação de identidade"
            data-testid="onboarding-identity-retry-error"
            action={
              <Button
                disabled={embedded.isLoading}
                onClick={() => void embedded.retry()}
                data-testid="onboarding-identity-retry-btn"
              >
                Tentar novamente
              </Button>
            }
          >
            {embedded.error}
          </Banner>
        ) : null}
        {embedded.session ? (
          <Suspense fallback={<Skeleton />}>
            <LazyEmbeddedOnboarding
              key={embedded.sessionKey}
              session={embedded.session}
              onExit={() => void embedded.close()}
              onRetry={embedded.retry}
            />
          </Suspense>
        ) : null}
      </Drawer>
    </>
  )
}

function VerificationSections({
  verification,
  enabled = true,
  activationPending = false,
  showBankAccount = true,
  onEditProfile,
  embedded,
}: VerificationPanelProps & {
  verification: VerificationQuery
  embedded: ReturnType<typeof useEmbeddedOnboarding>
}) {
  const syncVerification = useSyncKycVerification()
  const refreshing = verification.isFetching || syncVerification.isPending
  const identityContentId = useId()
  const [identityExpanded, setIdentityExpanded] = useState(false)

  const identityInReview = verification.data?.onboarding.steps.identity === 'in_review'
  const profile = useSession((state) => state.user?.profile)

  if (!enabled) {
    return (
      <Card id={IDENTITY_SECTION_ANCHOR} className={formCard}>
        <div className={sectionHeader}>
          <h2 className={sectionTitle} tabIndex={-1}>
            2. Verificação de identidade
          </h2>
          <StatusBadge
            size="sm"
            tone={activationPending ? 'informative' : 'neutral'}
            label={activationPending ? 'Preparando' : 'Indisponível'}
          />
        </div>
        <p className={sectionCopy}>
          {activationPending
            ? 'Preparando a verificação de identidade…'
            : 'Disponível após salvar os dados cadastrais.'}
        </p>
      </Card>
    )
  }

  if (verification.isPending) {
    return (
      <Card id={IDENTITY_SECTION_ANCHOR} className={formCard}>
        <div className={sectionHeader}>
          <h2 className={sectionTitle} tabIndex={-1}>
            2. Verificação de identidade
          </h2>
          <StatusBadge size="sm" tone="neutral" label="Pendente" />
        </div>
        <Skeleton />
      </Card>
    )
  }

  if (!verification.data || (verification.isError && !embedded.registrationFeedbackEnabled)) {
    return (
      <Card id={IDENTITY_SECTION_ANCHOR} className={formCard}>
        <div className={sectionHeader}>
          <h2 className={sectionTitle} tabIndex={-1}>
            2. Verificação de identidade
          </h2>
          <StatusBadge size="sm" tone="neutral" label="Pendente" />
        </div>
        <Banner tone="danger" title="Não foi possível consultar a verificação">
          <span>Atualize esta etapa para consultar as pendências.</span>{' '}
          <Button variant="secondary" onClick={() => void verification.refetch()}>
            Tentar novamente
          </Button>
        </Banner>
      </Card>
    )
  }

  const view = verification.data
  const staleFeedback =
    embedded.registrationFeedbackEnabled && verification.isError ? (
      <Banner
        tone="warning"
        title="Não foi possível consultar a verificação"
        data-testid="onboarding-identity-stale-feedback"
        action={
          <Button
            variant="secondary"
            disabled={refreshing}
            onClick={() => void verification.refetch()}
            data-testid="onboarding-identity-refresh-btn"
          >
            Tentar novamente
          </Button>
        }
      >
        A última situação conhecida continua visível.
      </Banner>
    ) : null
  // Opens only once the profile is approved, so the embedded flow never repeats the profile form.
  if (view.onboarding.steps.identity === 'unavailable') {
    return (
      <div id={IDENTITY_SECTION_ANCHOR} className={panel}>
        <Card className={formCard}>
          <div className={sectionHeader}>
            <h2 className={sectionTitle} tabIndex={-1}>
              2. Verificação de identidade
            </h2>
            <StatusBadge size="sm" tone="neutral" label="Indisponível" />
          </div>
          <p className={sectionCopy}>
            Disponível depois que os dados cadastrais estiverem salvos e corretos.
          </p>
          {staleFeedback}
        </Card>
        {showBankAccount && view.bank ? (
          <BankAccountSection
            bank={view.bank}
            identityPending={false}
            onboarding={view.onboarding}
          />
        ) : null}
      </div>
    )
  }

  const details = view.verification
  const embeddedRequirements = details?.requirements.embedded ?? []
  const status = onboardingStepStatus(
    view.onboarding.steps.identity,
    view.onboarding.badge === 'approved',
  )
  const identityDone = view.onboarding.steps.identity === 'done'
  const identityReason = identityReasonCopy(view.onboarding.identityReason ?? null, profile)
  const identityIsExpanded = !identityDone || identityExpanded
  const postExitFeedback =
    embedded.feedback === null
      ? null
      : embedded.feedbackUpdatedAt !== null &&
          verification.dataUpdatedAt === embedded.feedbackUpdatedAt
        ? embedded.feedback
        : feedbackFromView(
            view.onboarding,
            Boolean(details?.syncFailure),
            embeddedRequirements.length,
          )

  return (
    <div id={IDENTITY_SECTION_ANCHOR} className={panel}>
      <Card className={formCard}>
        <section className={section}>
          <div className={sectionHeader}>
            <h2 className={sectionTitle} tabIndex={-1}>
              2. Verificação de identidade
            </h2>
            <StatusBadge size="sm" tone={status.tone} label={status.label} />
          </div>
          {identityDone && !identityIsExpanded ? (
            <div className={completedSummary}>
              <span>Documento com foto e selfie · {status.label}</span>
              <Button
                variant="secondary"
                aria-expanded={false}
                aria-controls={identityContentId}
                onClick={() => setIdentityExpanded(true)}
              >
                Ver detalhes
              </Button>
            </div>
          ) : null}
        </section>
        {staleFeedback}
        {identityDone && postExitFeedback === 'done' ? (
          <Banner tone="success" title="Etapa concluída." />
        ) : null}
        <div id={identityContentId} hidden={identityDone && !identityIsExpanded}>
          <section className={section}>
            <p className={sectionCopy}>Confirme sua identidade para continuar o cadastro.</p>
            {identityReason !== null && postExitFeedback === null && !identityInReview ? (
              <Banner tone="warning" title={identityReason.title}>
                <p>{identityReason.message}</p>
                {onEditProfile ? (
                  <p>
                    Algum dado está errado?{' '}
                    <Button variant="secondary" size="sm" onClick={onEditProfile}>
                      Corrigir dados cadastrais
                    </Button>
                  </p>
                ) : null}
              </Banner>
            ) : null}
            <div className={requirementList}>
              <div className={requirementItem}>
                <span>Documento com foto e selfie</span>
              </div>
            </div>
            {embedded.isSyncing ? (
              <p className={copy} role="status">
                Atualizando seu cadastro…
              </p>
            ) : null}
            {!embedded.isSyncing && postExitFeedback === 'in_review' ? (
              <Banner tone="success" title="Recebemos seus documentos." />
            ) : null}
            {!embedded.isSyncing && postExitFeedback === 'needs_action' ? (
              <Banner
                tone="warning"
                title="Ainda faltam informações para concluir esta etapa."
                action={
                  <Button disabled={embedded.isLoading} onClick={() => void embedded.open()}>
                    Continuar
                  </Button>
                }
              />
            ) : null}
            {!embedded.isSyncing && postExitFeedback === 'error' ? (
              <div className={neutralFeedback} role="status">
                <span>Não conseguimos atualizar agora. Tente de novo em instantes.</span>
                <Button
                  variant="secondary"
                  disabled={refreshing}
                  onClick={() => syncVerification.mutate()}
                >
                  {refreshing ? 'Atualizando...' : 'Atualizar status'}
                </Button>
              </div>
            ) : null}
            {!embedded.isSyncing &&
            postExitFeedback === null &&
            embeddedRequirements.length > 0 &&
            !identityInReview ? (
              <div className={actionRow}>
                <Button disabled={embedded.isLoading} onClick={() => void embedded.open()}>
                  {embedded.isLoading ? 'Abrindo...' : 'Continuar'}
                </Button>
                {embedded.error && !embedded.registrationFeedbackEnabled ? (
                  <span role="alert">{embedded.error}</span>
                ) : null}
              </div>
            ) : null}
            {embedded.isSyncing === false && postExitFeedback === null && details === null ? (
              <p className={copy}>
                Estamos preparando esta etapa. Ela fica disponível em instantes.
              </p>
            ) : null}
            {identityDone ? (
              <div className={actionRow}>
                <Button
                  variant="secondary"
                  aria-expanded={identityIsExpanded}
                  aria-controls={identityContentId}
                  onClick={() => setIdentityExpanded(false)}
                >
                  Ocultar
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      </Card>

      {showBankAccount && view.bank ? (
        <BankAccountSection
          bank={view.bank}
          identityPending={identityInReview}
          onboarding={view.onboarding}
        />
      ) : null}
    </div>
  )
}

/** Why the identity step asks for a document; it explains the step's own action. */
function identityReasonCopy(
  reason: 'representative_authority' | 'data_not_confirmed' | null,
  profile: { firstName?: string; lastName?: string; legalName?: string } | null | undefined,
): { title: string; message: string } | null {
  if (reason === 'representative_authority') {
    const name = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ')
    const representative = name || 'o representante legal'
    const company = profile?.legalName || 'a empresa'
    return {
      title: 'Precisamos de um documento do representante legal',
      message: `Não conseguimos confirmar que ${representative} representa ${company}. Envie um documento que comprove a identidade do representante.`,
    }
  }
  if (reason === 'data_not_confirmed') {
    return {
      title: 'Precisamos de um documento para confirmar seus dados',
      message:
        'Não conseguimos confirmar alguns dados do cadastro. Envie o documento pedido para continuar.',
    }
  }
  return null
}
